import request from '@/scripts/request';
import type {
  ChatChatId,
  ChatPartial,
  ChatQuery,
  DBChatParams,
} from '@/contexts/chat/scripts/hooks/useChats/types';
import { type Chat, type DBChat } from '@/contexts/chat/scripts/hooks/useChats/types';
import {
  sendSessionDB,
  //sendEncryptionDB,
} from '@/scripts/dataBase';
import { ActionType } from '@/scripts/dataBase/type';
import type {
  ReqKeyPackage,
  ResKeyPackage,
} from '@/contexts/chat/scripts/hooks/useSingleEncryption/types';
import {
  genDBChat,
  genDBChats,
  genStateChat,
  genStateChats,
} from '@/contexts/chat/scripts/hooks/useChats/utils';
import type { MyAxiosResponseData } from '@/scripts/request/types';
import { getAvatar } from '@/scripts/utils/genAvatar';
import type { User } from '../user';

const chatDBApis = {
  getChats(userId: User['userId'], limit: number, offset: number) {
    return sendSessionDB<DBChat[]>(ActionType.querySessions, {
      tableName: userId,
      params: { limit, offset },
    });
  },
  getChat({ chat_id }: ChatQuery) {
    return sendSessionDB<Chat[]>(ActionType.querySessionByChatId, {
      params: chat_id,
    }).then((res) => res[0]);
  },
  async addChats(chatsParams: DBChatParams[]) {
    if (chatsParams.length) {
      await sendSessionDB(ActionType.addSessions, {
        params: chatsParams,
      });
    }
    return genDBChats(chatsParams);
  },
  async removeChats(ids: ChatChatId[]) {
    if (ids.length) {
      await sendSessionDB(ActionType.deleteSessions, {
        params: ids,
      });
    }
  },
  async updateChats(chatsParams: DBChatParams[]) {
    if (chatsParams.length) {
      await sendSessionDB(ActionType.updateSessions, {
        params: chatsParams,
      });
    }
  },
};

export const chatApis = {
  async getChats(userId: User['userId'], limit: number, offset: number) {
    return genStateChats(await chatDBApis.getChats(userId, limit, offset));
  },
  async getChat(params: ChatQuery): Promise<Chat | undefined> {
    const chat = await chatDBApis.getChat(params);
    return chat && genStateChat(chat);
  },
  async insertChats(chatsParams: DBChatParams[]) {
    return Promise.all(
      chatsParams.map(async (params) => {
        const old = await chatDBApis.getChat(params);
        const chat = genDBChat({ ...old, ...params });
        await chatDBApis.addChats([chat]);
        return chat;
      })
    );
  },
  async addChats(chatsParams: DBChatParams[]) {
    return chatDBApis.addChats(chatsParams);
  },
  async updateChats(chatsParams: DBChatParams[]) {
    return chatDBApis.updateChats(chatsParams);
  },
  async removeChats(ids: ChatChatId[]) {
    return chatDBApis.removeChats(ids);
  },
  async createChat(params: { user_id: string; expire_duration: number }) {
    return request<MyAxiosResponseData<{ chat_id: string }>>(`/api/v1/chat/create`, {
      method: 'POST',
      data: params,
    }).then((res) => {
      return res.data;
    });
  },
  getChatInfo(chat_id: ChatChatId) {
    return request(`/api/v1/chat/detail`, {
      params: { chatId: chat_id },
      disableErrorMsg: true,
    })
      .catch(() => {
        return null;
      })
      .then((res) => {
        return res?.data.user_id
          ? ({
              userId: res.data.user_id,
              name: res.data.name,
              avatar: getAvatar(res.data.avatar, res.data.name),
              createdAt: res.data.created_at,
              expire_duration: res.data.expire_duration,
            } as User)
          : undefined;
      });
  },
  setChatInfo(params: ChatQuery & ChatPartial) {
    return request(`/api/v1/chat/update`, {
      method: 'POST',
      data: params,
    });
  },
  sendKeyPackage(params: ReqKeyPackage) {
    return request(`/api/v1/user/keys`, {
      method: 'POST',
      data: params,
    });
  },
  getTargetKeyPackage(userId: string): Promise<ResKeyPackage> {
    return request(`/api/v1/user/keys/${userId}`).then((res) => {
      return res.data?.list?.at(-1) ?? {};
    });
  },
};
